/**
 * @module components/Dashboard/ActivityBadges
 * @description Insignias visuales de prioridad para controles ISO 27002.
 *
 * Exporta `PriorityBadge` — pill coloreado que indica la prioridad
 * (critical/high/medium/low) de un control. Usado en tablas y cards.
 *
 * @see ControlsTable y MetricsCards que usan este badge
 */
import React from 'react';
import { ISOControl } from '../../types/iso';

interface PriorityBadgeProps {
  priority: ISOControl['priority'];
}

export function PriorityBadge({ priority }: PriorityBadgeProps) {
  const styles = {
    critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
  };

  const labels = {
    critical: 'CRÍTICO',
    high: 'ALTA',
    medium: 'MEDIA',
    low: 'BAJA'
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${styles[priority]}`}>
      {labels[priority]}
    </span>
  );
}

interface StatusBadgeProps {
  status: ISOControl['status'];
}

export function StatusBadge({ status }: StatusBadgeProps) {
  const styles = {
    not_started: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    review: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    non_compliant: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
  };

  const labels = {
    not_started: 'No Iniciado',
    in_progress: 'En Progreso',
    review: 'En Revisión',
    completed: 'Completado',
    non_compliant: 'No Conforme'
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

interface ActionTypeBadgeProps {
  actionType: 'assignment' | 'status_change' | 'evidence_upload' | 'due_date_change' | 'task_created' | 'task_completed' | 'comment' | 'other';
}

export function ActionTypeBadge({ actionType }: ActionTypeBadgeProps) {
  const styles = {
    assignment: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    status_change: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    evidence_upload: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    due_date_change: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    task_created: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
    task_completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    comment: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
    other: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
  };

  const labels = {
    assignment: 'Asignación',
    status_change: 'Cambio Estado',
    evidence_upload: 'Evidencia',
    due_date_change: 'Fecha Límite',
    task_created: 'Tarea Creada',
    task_completed: 'Tarea Completada',
    comment: 'Comentario',
    other: 'Otro'
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${styles[actionType]}`}>
      {labels[actionType]}
    </span>
  );
}
